// src/components/ModalPagoVenta.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner, Table } from "react-bootstrap";
import "animate.css";

import {
  getPagosVenta,
  postPagoVenta,
  putPagoVenta,
  deletePagoVenta,
  acreditarPagoVenta,
  marcarContratoFirmadoPagoVenta,
  MEDIOS_PAGO_VENTA,
} from "../helpers/rutaPagoVentas";

import { getClientes } from "../helpers/rutaClientes";

import { getFinancieras } from "../helpers/rutaFinancieras";
import ModalGaranteFinanciacion from "./ModalGaranteFinanciacion";
import "../css/venta-rapida-wizard.css";

/* ---------------- utils ---------------- */

const clean = (v) => String(v ?? "").trim();

const upper = (v) =>
  clean(v).toUpperCase();

const getId = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return clean(v._id ?? v.id ?? v.value ?? "");
  }

  return clean(v);
};

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const moneyUSD = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toDateInputValue = (v) => {
  if (!v) return "";

  /*
    Las fechas de pago son fechas calendario,
    no horas.

    Si backend devuelve:
    2026-08-20T00:00:00.000Z

    debemos conservar:
    2026-08-20

    sin convertir zona horaria.
  */
  const texto = String(v).trim();

  const match =
    texto.match(
      /^(\d{4}-\d{2}-\d{2})/
    );

  if (match) {
    return match[1];
  }

  const d = new Date(v);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "";
  }

  return d
    .toISOString()
    .slice(0, 10);
};

const getNombreCliente = (cliente = {}) => {
  const armado = [cliente.apellido1, cliente.apellido2, cliente.nombre]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(cliente.razonsocial) || "-";
};

const getNombreFinanciera = (financiera) => {
  if (!financiera) return "-";

  if (typeof financiera === "string") return financiera;

  return clean(financiera.financiera || financiera.razonsocial) || "-";
};

const normalizarNombreFinanciera = (v = "") =>
  String(v || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

const getFinancieraById = (financieraId, financieras = []) => {
  const id = getId(financieraId);
  if (!id) return null;

  return financieras.find((f) => getId(f) === id) || null;
};

const getNombreFinancieraPago = (pago = {}, financieras = []) => {
  const fLista = getFinancieraById(
    pago.financiera,
    financieras
  );

  /*
    Si la financiera ya fue desactivada puede no venir
    en getFinancieras(), pero el pago puede traerla
    populateada desde el backend.

    Usamos ambas fuentes para no perder la regla de Tax
    en pagos históricos.
  */
  const fPago =
    pago?.financiera &&
    typeof pago.financiera === "object"
      ? pago.financiera
      : null;

  return normalizarNombreFinanciera(
    fLista?.financiera ||
      fLista?.razonsocial ||
      fLista?.nombre ||
      fPago?.financiera ||
      fPago?.razonsocial ||
      fPago?.nombre ||
      ""
  );
};

const TAX_PORCENTAJE_DEFAULT = 7;

const FINANCIERAS_SIN_TAX = new Set([
  "LENDMARK - VIP WHEELS",
  "SNAP - VIP WHEELS",
  "PROGRESSIVE - VIP WHEELS",
  "KOALA-FI - VIP WHEELS",
  "KAFENE - VIP WHEELS",
  "EASYPAY - VIP WHEELS",
  "KIWI PAY - VIP WHEELS",
]);

const esEfectivoEnTienda = (medioPago = "") =>
  clean(medioPago).toUpperCase() === "EFECTIVO EN TIENDA";

const esFinancieraSinTax = (
  pago = {},
  financieras = []
) => {
  if (
    upper(pago?.medioPago) !==
    "CREDITO FINANCIERA"
  ) {
    return false;
  }

  /*
    ======================================================
    PAGO NUEVO CON SNAPSHOT
    ======================================================

    Si ya existe la decisión de Tax guardada
    en el pago, esa decisión manda siempre.
  */
  if (
    typeof pago?.aplicaTaxFinanciera ===
    "boolean"
  ) {
    return (
      pago.aplicaTaxFinanciera ===
      false
    );
  }

  /*
    ======================================================
    PAGO HISTÓRICO
    ======================================================

    Un pago persistido sin snapshot pertenece
    a la lógica anterior.

    También usamos esta condición cuando estamos
    editando uno histórico y lo marcamos
    expresamente desde editarPago().
  */
  const esHistorico =
    pago?._forzarLegacyHistorico ===
      true ||
    (
      Boolean(getId(pago)) &&
      typeof pago
        ?.aplicaTaxFinanciera !==
        "boolean"
    );

  /*
    ======================================================
    PAGO NUEVO - CONFIGURACIÓN ACTUAL
    ======================================================

    Solo para un pago NUEVO usamos el valor
    configurado actualmente en Administración.
  */
  if (!esHistorico) {
    const financiera =
      getFinancieraById(
        pago.financiera,
        financieras
      );

    if (
      financiera
        ?.configuracionPagosActiva ===
        true
    ) {
      return (
        financiera.aplicaTax ===
        false
      );
    }
  }

  /*
    ======================================================
    LÓGICA HISTÓRICA
    ======================================================
  */
  const nombre =
    getNombreFinancieraPago(
      pago,
      financieras
    );

  return FINANCIERAS_SIN_TAX.has(
    nombre
  );
};

const esPagoSinTax = (
  pago = {},
  financieras = []
) =>
  esEfectivoEnTienda(pago?.medioPago) ||
  esFinancieraSinTax(pago, financieras);

const permiteResponsablePago = (medioPago = "") => {
  const medio = clean(medioPago).toUpperCase();

  return [
    "CREDITO FINANCIERA",
    "CREDITO",
    "DEBITO",
  ].includes(medio);
};

const calcularFeePagoVenta = (
  pago = {},
  financieras = []
) => {
  const medio =
    upper(pago.medioPago);

  const montoUSD =
    round2(
      Math.max(
        0,
        toNumber(
          pago.montoUSD,
          0
        )
      )
    );

  /*
    ======================================================
    TARJETA DE CRÉDITO
    ======================================================
  */
  if (medio === "CREDITO") {
    const feePorcentaje = 3;

    const feeMontoUSD =
      round2(
        montoUSD *
          (feePorcentaje / 100)
      );

    return {
      configuracionNueva:
        false,

      tipoCalculo:
        "MONTO_DIRECTO",

      esFondeoInverso:
        false,

      requiereCuotas:
        false,

      cuotas:
        0,

      cuotasDisponibles:
        [],

      feePorcentaje,

      feeMontoUSD,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD:
        montoUSD,

      montoFinancieraUSD:
        0,

      totalCobrarClienteUSD:
        round2(
          montoUSD +
            feeMontoUSD
        ),
    };
  }

  /*
    ======================================================
    EFECTIVO / ZELLE / DÉBITO
    ======================================================
  */
  if (
    medio !==
    "CREDITO FINANCIERA"
  ) {
    return {
      configuracionNueva:
        false,

      tipoCalculo:
        "MONTO_DIRECTO",

      esFondeoInverso:
        false,

      requiereCuotas:
        false,

      cuotas:
        0,

      cuotasDisponibles:
        [],

      feePorcentaje:
        0,

      feeMontoUSD:
        0,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD:
        montoUSD,

      montoFinancieraUSD:
        0,

      totalCobrarClienteUSD:
        montoUSD,
    };
  }

  /*
    ======================================================
    FINANCIERA
    ======================================================
  */

  const financiera =
    getFinancieraById(
      pago.financiera,
      financieras
    );

  /*
    Cuando editamos un pago histórico podemos
    forzar la lógica vieja aunque actualmente
    la financiera ya esté migrada.
  */
  const configuracionNueva =
    financiera
      ?.configuracionPagosActiva ===
      true &&
    pago?._forzarLegacyHistorico !==
      true;

  const tipoCalculo =
    upper(
      financiera?.tipoCalculo ||
        "GROSS_UP"
    );

  const cuotas =
    toNumber(
      pago.cuotasFinanciacion,
      0
    );

  /*
    ======================================================
    NUEVA CONFIGURACIÓN
    ======================================================
  */

  if (configuracionNueva) {
    const reglas =
      Array.isArray(
        financiera?.reglasCuotas
      )
        ? financiera.reglasCuotas
        : [];

    const requiereCuotas =
      Boolean(
        financiera
          ?.requiereCuotas
      ) ||
      tipoCalculo ===
        "POR_CUOTA";

    const cuotasDisponibles =
      reglas
        .map((r) =>
          Number(r?.cuotas)
        )
        .filter(
          (n) =>
            Number.isFinite(n) &&
            n > 0
        );

    let feePorcentaje =
      Math.max(
        0,
        toNumber(
          financiera
            ?.feePorcentaje,
          0
        )
      );

    if (
      requiereCuotas &&
      cuotas > 0
    ) {
      const regla =
        reglas.find(
          (r) =>
            Number(
              r?.cuotas
            ) === cuotas
        );

      if (regla) {
        feePorcentaje =
          Math.max(
            0,
            toNumber(
              regla
                ?.feePorcentaje,
              0
            )
          );
      }
    }

    /*
      ====================================================
      FONDEO INVERSO
      ====================================================
    */

    if (
      tipoCalculo ===
      "FONDEO_INVERSO"
    ) {
      const fondeoFinancieraUSD =
        round2(
          Math.max(
            0,
            toNumber(
              pago
                .fondeoFinancieraUSD,
              0
            )
          )
        );

      const feeMontoUSD =
        round2(
          fondeoFinancieraUSD *
            (
              feePorcentaje /
              100
            )
        );

      /*
        Neto REAL que deja la financiera.
      */
      const montoNetoFinancieraUSD =
        round2(
          Math.max(
            fondeoFinancieraUSD -
              feeMontoUSD,
            0
          )
        );

      /*
        El modal puede forzar el importe
        que debe aplicarse realmente a la venta.

        Si no viene forzado usamos inicialmente
        el neto real de la financiera.
      */
      const tieneMontoForzado =
        Object.prototype.hasOwnProperty.call(
          pago,
          "montoAplicadoForzadoUSD"
        );

      const montoForzadoUSD =
        round2(
          Math.max(
            0,
            toNumber(
              pago
                .montoAplicadoForzadoUSD,
              0
            )
          )
        );

      const montoAplicadoUSD =
        tieneMontoForzado
          ? montoForzadoUSD
          : montoNetoFinancieraUSD;

      const factorNeto =
        1 -
        feePorcentaje / 100;

      /*
        Fondeo correcto esperado para cubrir
        exactamente lo aplicado a la venta.
      */
      const fondeoEsperadoUSD =
        factorNeto > 0
          ? round2(
              montoAplicadoUSD /
                factorNeto
            )
          : montoAplicadoUSD;

      /*
        + = financiera dejó de más
        - = financiera dejó de menos
      */
      const diferenciaFinancieraUSD =
        round2(
          montoNetoFinancieraUSD -
            montoAplicadoUSD
        );

      /*
        Si falta dinero queda registrado
        como ajuste financiero.
      */
      const ajusteFinancieroUSD =
        round2(
          Math.max(
            montoAplicadoUSD -
              montoNetoFinancieraUSD,
            0
          )
        );

      return {
        configuracionNueva:
          true,

        tipoCalculo,

        esFondeoInverso:
          true,

        requiereCuotas,

        cuotas,

        cuotasDisponibles,

        feePorcentaje,

        feeMontoUSD,

        fondeoFinancieraUSD,

        fondeoEsperadoUSD,

        diferenciaFinancieraUSD,

        ajusteFinancieroUSD,

        montoAplicadoUSD,

        /*
          Neto REAL financiero.
        */
        montoFinancieraUSD:
          montoNetoFinancieraUSD,

        totalCobrarClienteUSD:
          fondeoFinancieraUSD,
      };
    }

    /*
      ====================================================
      MONTO DIRECTO
      ====================================================
    */

    if (
      tipoCalculo ===
      "MONTO_DIRECTO"
    ) {
      const feeMontoUSD =
        round2(
          montoUSD *
            (
              feePorcentaje /
              100
            )
        );

      return {
        configuracionNueva:
          true,

        tipoCalculo,

        esFondeoInverso:
          false,

        requiereCuotas,

        cuotas,

        cuotasDisponibles,

        feePorcentaje,

        feeMontoUSD,

        fondeoFinancieraUSD:
          0,

        montoAplicadoUSD:
          montoUSD,

        montoFinancieraUSD:
          montoUSD,

        totalCobrarClienteUSD:
          round2(
            montoUSD +
              feeMontoUSD
          ),
      };
    }

    /*
      ====================================================
      GROSS UP / POR CUOTA
      ====================================================
    */

    const factorNeto =
      1 -
      feePorcentaje / 100;

    const totalCobrarClienteUSD =
      feePorcentaje > 0 &&
      factorNeto > 0
        ? round2(
            montoUSD /
              factorNeto
          )
        : montoUSD;

    const feeMontoUSD =
      round2(
        totalCobrarClienteUSD -
          montoUSD
      );

    return {
      configuracionNueva:
        true,

      tipoCalculo,

      esFondeoInverso:
        false,

      requiereCuotas,

      cuotas,

      cuotasDisponibles,

      feePorcentaje,

      feeMontoUSD,

      fondeoFinancieraUSD:
        0,

      montoAplicadoUSD:
        montoUSD,

      montoFinancieraUSD:
        montoUSD,

      totalCobrarClienteUSD,
    };
  }

  /*
    ======================================================
    LÓGICA HISTÓRICA
    ======================================================
  */

  const nombre =
    getNombreFinancieraPago(
      pago,
      financieras
    );

  let feePorcentaje = 0;
  let requiereCuotas = false;

  let cuotasDisponibles = [];

  if (
    nombre.includes("ACIMA")
  ) {
    feePorcentaje = 0;
  } else if (
    nombre ===
    "AFF - VIP WHEELS"
  ) {
    feePorcentaje = 15;
  } else if (
    nombre ===
    "KIWI PAY - VIP WHEELS"
  ) {
    feePorcentaje = 18;
  } else if (
    [
      "LENDMARK - VIP WHEELS",
      "SNAP - VIP WHEELS",
      "PROGRESSIVE - VIP WHEELS",
      "KOALA-FI - VIP WHEELS",
      "KAFENE - VIP WHEELS",
      "EASYPAY - VIP WHEELS",
    ].includes(nombre)
  ) {
    feePorcentaje = 10;
  } else if (
    nombre.includes("SNAP")
  ) {
    feePorcentaje = 3;
  } else if (
    nombre.includes(
      "SYNCHRONY"
    ) ||
    nombre.includes(
      "SYNCRONY"
    ) ||
    nombre.includes(
      "SINCRONIA"
    ) ||
    nombre.includes(
      "SINCRON"
    )
  ) {
    requiereCuotas = true;

    cuotasDisponibles = [
      12,
      18,
      24,
    ];

    if (cuotas === 12) {
      feePorcentaje = 3;
    } else if (
      cuotas === 18
    ) {
      feePorcentaje = 6;
    } else if (
      cuotas === 24
    ) {
      feePorcentaje = 9;
    }
  } else if (
    (
      nombre.includes(
        "USBANK"
      ) ||
      nombre.includes(
        "US BANK"
      )
    ) &&
    nombre.includes(
      "EVERLY"
    )
  ) {
    feePorcentaje = 8;
  } else if (
    (
      nombre.includes(
        "USBANK"
      ) ||
      nombre.includes(
        "US BANK"
      )
    ) &&
    nombre.includes(
      "CARITAS"
    )
  ) {
    feePorcentaje = 4;
  } else if (
    nombre.includes(
      "USBANK"
    ) ||
    nombre.includes(
      "US BANK"
    )
  ) {
    feePorcentaje = 4;
  }

  const factorNeto =
    1 -
    feePorcentaje / 100;

  const totalCobrarClienteUSD =
    feePorcentaje > 0 &&
    factorNeto > 0
      ? round2(
          montoUSD /
            factorNeto
        )
      : montoUSD;

  const feeMontoUSD =
    round2(
      totalCobrarClienteUSD -
        montoUSD
    );

  return {
    configuracionNueva:
      false,

    tipoCalculo:
      "GROSS_UP",

    esFondeoInverso:
      false,

    requiereCuotas,

    cuotas,

    cuotasDisponibles,

    feePorcentaje,

    feeMontoUSD,

    fondeoFinancieraUSD:
      0,

    montoAplicadoUSD:
      montoUSD,

    montoFinancieraUSD:
      montoUSD,

    totalCobrarClienteUSD,
  };
};

const estadoBadge = (estado) => {
  const e = clean(estado).toUpperCase();

  if (e === "PAGADO" || e === "ACREDITADO") return "badge bg-success";
  if (e === "PARCIAL") return "badge bg-warning text-dark";
  if (e === "PENDIENTE") return "badge bg-danger";

  return "badge bg-secondary";
};

const initialPago = {
  medioPago: "EFECTIVO EN TIENDA",

  montoUSD: "",

  fondeoFinancieraUSD: "",

  /*
    Snapshot de Tax.
    null = pago nuevo / histórico sin snapshot.
  */
  aplicaTaxFinanciera: null,

  /*
    Solo uso interno del modal.
    No se envían al backend.
  */
  _forzarLegacyHistorico: false,
  _financieraOriginalId: "",

  financiera: "",

  clienteFinanciacion: "",

  cuotasFinanciacion: "",

  estadoAcreditacion:
    "ACREDITADO",

  fechaPago:
    todayLocalISO(),

  observaciones: "",
};

export default function ModalPagoVenta({
  show,
  handleClose,
  venta,
  onSaved,
  soloLectura = false,
  esManagerMiami = false,
}) {
  const ventaId = getId(venta);
  const [pagos, setPagos] = useState([]);
  const [financieras, setFinancieras] = useState([]);

  const [ventaActual, setVentaActual] = useState(null);
  const [resumenPago, setResumenPago] = useState(null);

  const [formPago, setFormPago] = useState(initialPago);
  const [pagoEditando, setPagoEditando] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [qClienteFinanciacion, setQClienteFinanciacion] = useState("");

  const [showGaranteIA, setShowGaranteIA] = useState(false);

  const esCreditoFinanciera =
    clean(formPago.medioPago).toUpperCase() === "CREDITO FINANCIERA";

  const feePreviewBaseFormulario = useMemo(
    () =>
      calcularFeePagoVenta(
        formPago,
        financieras
      ),
    [formPago, financieras]
  );

  const ventaBase = ventaActual || venta || {};

  const totalVenta = round2(
    resumenPago?.totalVenta ??
      ventaBase?.totalUSD ??
      ventaBase?.total ??
      0
  );

  const totalPagado = round2(
    resumenPago?.totalPagadoUSD ??
      ventaBase?.totalPagadoUSD ??
      pagos.reduce((acc, p) => acc + toNumber(p.montoUSD, 0), 0)
  );

  const totalAcreditado = round2(
    resumenPago?.totalAcreditadoUSD ??
      ventaBase?.totalAcreditadoUSD ??
      pagos
        .filter((p) => p.estadoAcreditacion === "ACREDITADO")
        .reduce((acc, p) => acc + toNumber(p.montoUSD, 0), 0)
  );

  const saldoPendiente = round2(
    resumenPago?.saldoPendienteUSD ??
      ventaBase?.saldoPendienteUSD ??
      Math.max(totalVenta - totalAcreditado, 0)
  );

/*
  =========================================================
  PREVIEW DE TAX / SALDO
  =========================================================

  No pagan Tax:
  - EFECTIVO EN TIENDA
  - financieras incluidas en FINANCIERAS_SIN_TAX

  Para edición quitamos primero el pago que se está
  modificando y luego simulamos nuevamente el cálculo.
*/

const montoEditandoActual =
  pagoEditando
    ? round2(
        Math.max(
          toNumber(
            pagoEditando.montoUSD,
            0
          ),
          0
        )
      )
    : 0;

const pagoEditandoId =
  getId(pagoEditando);

const pagosSinPagoEditando =
  pagos.filter((pago) => {
    const estado =
      clean(
        pago?.estadoAcreditacion
      ).toUpperCase();

    if (estado === "ANULADO") {
      return false;
    }

    if (
      pagoEditandoId &&
      getId(pago) ===
        pagoEditandoId
    ) {
      return false;
    }

    return true;
  });

const totalAplicadoAntesFormulario =
  round2(
    Math.max(
      totalPagado -
        montoEditandoActual,
      0
    )
  );

const totalProductosVenta =
  round2(
    Math.max(
      toNumber(
        ventaBase
          ?.totalProductosUSD,
        0
      ),
      0
    )
  );

const totalEnvioVenta =
  round2(
    Math.max(
      toNumber(
        ventaBase
          ?.totalEnvioUSD,
        0
      ),
      0
    )
  );

const taxAplicadoVenta =
  ventaBase?.taxAplicado ===
  true;

const porcentajeGuardado =
  Number(
    ventaBase?.taxPorcentaje
  );

const taxPorcentajeVenta =
  Number.isFinite(
    porcentajeGuardado
  )
    ? porcentajeGuardado
    : TAX_PORCENTAJE_DEFAULT;

const tasaTax =
  taxPorcentajeVenta / 100;

/*
  Pagos anteriores que no pagan Tax,
  excluyendo el pago que estamos editando.
*/
const totalPagosSinTaxAntesFormulario =
  round2(
    pagosSinPagoEditando
      .filter((pago) =>
        esPagoSinTax(
          pago,
          financieras
        )
      )
      .reduce(
        (acc, pago) =>
          acc +
          Math.max(
            toNumber(
              pago?.montoUSD,
              0
            ),
            0
          ),
        0
      )
  );

const formularioEsSinTax =
  esPagoSinTax(
    formPago,
    financieras
  );

/*
  Solo recalculamos Tax para ventas
  que poseen la estructura nueva.
*/
const puedeRecalcularTaxPreview =
  taxAplicadoVenta &&
  totalProductosVenta > 0 &&
  tasaTax >= 0;

const baseTaxAntesFormulario =
  puedeRecalcularTaxPreview
    ? round2(
        Math.max(
          totalProductosVenta -
            totalPagosSinTaxAntesFormulario,
          0
        )
      )
    : 0;

const taxAntesFormularioUSD =
  puedeRecalcularTaxPreview
    ? round2(
        baseTaxAntesFormulario *
          tasaTax
      )
    : 0;

/*
  =====================================================
  MÁXIMO QUE PUEDE APLICARSE A ESTA VENTA
  =====================================================
*/
let maxMontoFormulario =
  round2(
    Math.max(
      totalVenta -
        totalAplicadoAntesFormulario,
      0
    )
  );

if (puedeRecalcularTaxPreview) {
  const totalVentaAntesFormulario =
    round2(
      totalProductosVenta +
        totalEnvioVenta +
        taxAntesFormularioUSD
    );

  if (formularioEsSinTax) {
    const disponibleAntesTax =
      round2(
        Math.max(
          totalVentaAntesFormulario -
            totalAplicadoAntesFormulario,
          0
        )
      );

    const baseDisponibleTax =
      baseTaxAntesFormulario;

    const tramoConTax =
      tasaTax > 0
        ? round2(
            disponibleAntesTax /
              (1 + tasaTax)
          )
        : disponibleAntesTax;

    if (
      tramoConTax <=
      baseDisponibleTax
    ) {
      maxMontoFormulario =
        tramoConTax;
    } else {
      maxMontoFormulario =
        round2(
          Math.max(
            totalProductosVenta +
              totalEnvioVenta -
              totalAplicadoAntesFormulario,
            0
          )
        );
    }
  } else {
    maxMontoFormulario =
      round2(
        Math.max(
          totalVentaAntesFormulario -
            totalAplicadoAntesFormulario,
          0
        )
      );
  }
}

/*
  Pago nuevo:
  Fondeo Inverso cubre el saldo disponible.

  Pago en edición:
  conservamos el monto que ya estaba aplicado
  a la venta para no modificarlo accidentalmente
  solo por corregir el fondeo informado.
*/
const montoObjetivoFondeoFormulario =
  pagoEditando &&
  montoEditandoActual > 0
    ? round2(
        Math.min(
          montoEditandoActual,
          maxMontoFormulario
        )
      )
    : maxMontoFormulario;

/*
  =====================================================
  FONDEO INVERSO

  En el modal, el pago financiero se aplica al
  saldo disponible de la venta.

  El fondeo puede estar bien, arriba o abajo;
  eso genera la diferencia financiera.
  =====================================================
*/
const feePreviewFormulario =
  useMemo(() => {
    const fondeo =
      toNumber(
        formPago
          .fondeoFinancieraUSD,
        0
      );

    if (
      !feePreviewBaseFormulario
        ?.esFondeoInverso ||
      fondeo <= 0
    ) {
      return feePreviewBaseFormulario;
    }

    return calcularFeePagoVenta(
      {
        ...formPago,

        montoAplicadoForzadoUSD:
          montoObjetivoFondeoFormulario,
      },
      financieras
    );
  },[
      formPago,
      financieras,
      feePreviewBaseFormulario,
      montoObjetivoFondeoFormulario,
    ]
);

const montoFormularioActual =
  round2(
    Math.max(
      toNumber(
        feePreviewFormulario
          ?.montoAplicadoUSD,
        0
      ),
      0
    )
  );

const totalPagosSinTaxPreview =
  round2(
    totalPagosSinTaxAntesFormulario +
      (
        formularioEsSinTax
          ? montoFormularioActual
          : 0
      )
  );

const taxBasePreviewUSD =
  puedeRecalcularTaxPreview
    ? round2(
        Math.max(
          totalProductosVenta -
            totalPagosSinTaxPreview,
          0
        )
      )
    : 0;

const taxMontoPreviewUSD =
  puedeRecalcularTaxPreview
    ? round2(
        taxBasePreviewUSD *
          tasaTax
      )
    : round2(
        Math.max(
          toNumber(
            ventaBase
              ?.taxMontoUSD,
            0
          ),
          0
        )
      );

/*
  Tax que elimina específicamente
  el pago que estamos cargando.
*/
const taxNoAplicadoPagoPreviewUSD =
  formularioEsSinTax &&
  puedeRecalcularTaxPreview
    ? round2(
        Math.max(
          taxAntesFormularioUSD -
            taxMontoPreviewUSD,
          0
        )
      )
    : 0;

const totalVentaPreview =
  puedeRecalcularTaxPreview
    ? round2(
        totalProductosVenta +
          totalEnvioVenta +
          taxMontoPreviewUSD
      )
    : totalVenta;

const totalAplicadoPreview =
  round2(
    totalAplicadoAntesFormulario +
      montoFormularioActual
  );

const saldoPendientePreview =
  round2(
    Math.max(
      totalVentaPreview -
        totalAplicadoPreview,
      0
    )
  );

const saldoDisponibleCarga =
  maxMontoFormulario;

  const formularioBloqueado =
    soloLectura ||
    saving ||
    (!pagoEditando && saldoDisponibleCarga <= 0);

  const resumenCliente = useMemo(() => {
    const totalFeeClienteUSD = round2(
      pagos.reduce((acc, p) => acc + toNumber(p.feeMontoUSD, 0), 0)
    );

    const totalCobraClienteUSD = round2(
      pagos.reduce((acc, p) => {
        const monto = toNumber(p.montoUSD, 0);
        const fee = toNumber(p.feeMontoUSD, 0);
        const totalCliente = toNumber(p.totalCobrarClienteUSD, 0);

        return acc + (totalCliente > 0 ? totalCliente : round2(monto + fee));
      }, 0)
    );

    return {
      totalFeeClienteUSD,
      totalCobraClienteUSD,
    };
  }, [pagos]);

  const estadoPago =
    resumenPago?.estadoPago || ventaBase?.estadoPago || "PENDIENTE";

  const nroVenta = ventaBase?.nroventa || "-";

  const clienteNombre = useMemo(() => {
    return getNombreCliente(ventaBase?.clienteCompra);
  }, [ventaBase]);

  const getNombreClienteFinanciacionPago = (pago = {}) => {
    if (
      !permiteResponsablePago(
        pago?.medioPago
      )
    ) {
      return "-";
    }
    const cf = pago?.clienteFinanciacion;

    // Si viene populateado como objeto
    if (cf && typeof cf === "object") {
      return getNombreCliente(cf);
    }

    // Si viene solo como ObjectId/string
    const idClienteFinanciacion = getId(cf);

    if (idClienteFinanciacion) {
      const encontrado = clientes.find(
        (c) => getId(c) === idClienteFinanciacion
      );

      if (encontrado) {
        return getNombreCliente(encontrado);
      }

      return "CLIENTE FINANCIACIÓN NO ENCONTRADO";
    }

    // Solo si realmente no hay clienteFinanciacion, uso el comprador
    return clienteNombre || "-";
  };

  const clienteCompraId = getId(ventaBase?.clienteCompra);

  const clientesFiltrados = useMemo(() => {
    const q = clean(
      qClienteFinanciacion
    ).toUpperCase();

    const lista =
      Array.isArray(clientes)
        ? clientes
        : [];

    if (!q) {
      return lista;
    }

    return lista.filter((c) => {
      const texto = [
        c.codcli,
        c.razonsocial,
        c.nombre,
        c.apellido1,
        c.apellido2,

        c.dni,
        c.documento,
        c.idDocumento,

        c.telefono,
        c.telefono1,
        c.telefonoPrincipal,

        c.email,
      ]
        .join(" ")
        .toUpperCase();

      return texto.includes(q);
    });
  }, [
    clientes,
    qClienteFinanciacion,
]);

const abrirGaranteIA = () => {
  if (formularioBloqueado) {
    return;
  }

  setShowGaranteIA(true);
};

const cerrarGaranteIA = () => {
  setShowGaranteIA(false);
};

const onGaranteIAGuardado = (
  garanteCreado
) => {
  const id =
    getId(garanteCreado);

  if (!id) {
    alert(
      "El garante se guardó, pero no se pudo obtener su ID."
    );

    return;
  }

  /*
    Lo incorporamos inmediatamente
    a la lista local.
  */
  setClientes((prev) => {
    const lista =
      Array.isArray(prev)
        ? prev
        : [];

    const existe =
      lista.some(
        (cliente) =>
          getId(cliente) === id
      );

    if (existe) {
      return lista.map(
        (cliente) =>
          getId(cliente) === id
            ? {
                ...cliente,
                ...garanteCreado,
              }
            : cliente
      );
    }

    return [
      garanteCreado,
      ...lista,
    ];
  });

  /*
    Y queda seleccionado solamente
    en ESTE pago.
  */
  setFormPago((prev) => ({
    ...prev,

    clienteFinanciacion:
      id,
  }));

  setQClienteFinanciacion("");

  setShowGaranteIA(false);
};

  /* ---------------- cargar datos ---------------- */

  const cargarDatos = async () => {
    if (!ventaId) return;

    try {
      setLoading(true);
      setError("");

    const [
      respPagos,
      respFinancieras,
      respClientesPrimera,
    ] = await Promise.all([
      getPagosVenta(ventaId),

      getFinancieras({
        page: 1,
        limit: 300,
      }),

      getClientes({
        page: 1,
        limit: 200,
      }),
    ]);

    /*
      El backend de clientes permite
      máximo 200 registros por página.

      Traemos las páginas restantes para
      que el selector de garante pueda
      buscar entre todos los clientes.
    */

    let todosClientes =
      Array.isArray(
        respClientesPrimera?.clientes
      )
        ? [...respClientesPrimera.clientes]
        : [];

    const totalClientes =
      Number(
        respClientesPrimera?.total ||
          todosClientes.length
      );

    const totalPaginas =
      Math.max(
        1,
        Math.ceil(totalClientes / 200)
      );

    if (totalPaginas > 1) {
      const pedidos = [];

      for (
        let page = 2;
        page <= totalPaginas;
        page++
      ) {
        pedidos.push(
          getClientes({
            page,
            limit: 200,
          })
        );
      }

      const respuestas =
        await Promise.all(pedidos);

      respuestas.forEach((resp) => {
        if (
          Array.isArray(resp?.clientes)
        ) {
          todosClientes.push(
            ...resp.clientes
          );
        }
      });
    }

    /*
      Evitamos cualquier duplicado
      accidental por _id.
    */
    const clientesUnicos =
      Array.from(
        new Map(
          todosClientes.map(
            (cliente) => [
              getId(cliente),
              cliente,
            ]
          )
        ).values()
      );

    setClientes(clientesUnicos);

      setPagos(Array.isArray(respPagos?.pagos) ? respPagos.pagos : []);
      setVentaActual(respPagos?.venta || null);
      setResumenPago(respPagos?.resumenPago || null);

      setFinancieras(
        Array.isArray(respFinancieras?.financieras)
          ? respFinancieras.financieras
          : []
      );
    } catch (err) {
      console.error("❌ Error cargando pagos de venta:", err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudieron cargar los pagos."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    setFormPago(initialPago);
    setPagoEditando(null);
    setError("");
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, ventaId]);

  /* ---------------- form pago ---------------- */

  const handleChangePago = (e) => {
    const { name, value } = e.target;

    setFormPago((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "medioPago") {
        const medio = upper(value);

        /*
          Si el operador cambia el medio,
          ya deja de ser una simple edición
          histórica del mismo pago.
        */
        next._forzarLegacyHistorico = false;
        next.aplicaTaxFinanciera = null;

        /*
          FINANCIERA
        */
        if (
          medio ===
          "CREDITO FINANCIERA"
        ) {
          next.estadoAcreditacion =
            "PENDIENTE";

          next.fondeoFinancieraUSD =
            "";

          if (
            !next.clienteFinanciacion
          ) {
            next.clienteFinanciacion =
              clienteCompraId;
          }
        }

        /*
          CRÉDITO / DÉBITO
        */
        else if (
          medio === "CREDITO" ||
          medio === "DEBITO"
        ) {
          next.financiera = "";
          next.cuotasFinanciacion = "";
          next.fondeoFinancieraUSD = "";

          next.estadoAcreditacion =
            "ACREDITADO";

          if (
            !next.clienteFinanciacion
          ) {
            next.clienteFinanciacion =
              clienteCompraId;
          }
        }

        /*
          EFECTIVO / ZELLE / OTROS
        */
        else {
          next.financiera = "";
          next.clienteFinanciacion = "";
          next.cuotasFinanciacion = "";
          next.fondeoFinancieraUSD = "";

          next.estadoAcreditacion =
            "ACREDITADO";
        }
      }

      if (name === "financiera") {
        next.cuotasFinanciacion = "";
        next.fondeoFinancieraUSD = "";

        /*
          Si se cambia explícitamente de
          financiera usamos la configuración
          actual de esa financiera.
        */
        next._forzarLegacyHistorico =
          false;

        next.aplicaTaxFinanciera =
          null;

        const financieraNueva =
          getFinancieraById(
            value,
            financieras
          );

        const usaFondeo =
          financieraNueva
            ?.configuracionPagosActiva ===
            true &&
          upper(
            financieraNueva
              ?.tipoCalculo
          ) ===
            "FONDEO_INVERSO";

        /*
          Para Fondeo Inverso el vendedor
          ya no ingresa montoUSD.
        */
        if (usaFondeo) {
          next.montoUSD = "";
        }
      }

      return next;
    });
  };

  const limpiarFormularioPago = () => {
    setFormPago(initialPago);
    setPagoEditando(null);
    setQClienteFinanciacion("");
  };

  const validarPago = () => {
    if (!ventaId) {
      return "No se encontró el ID de la venta.";
    }

    const medioPago =
      upper(formPago.medioPago);

    if (!medioPago) {
      return "Seleccioná un medio de pago.";
    }

    if (
      medioPago ===
        "CREDITO FINANCIERA" &&
      !clean(formPago.financiera)
    ) {
      return "Seleccioná una financiera.";
    }

    const montoAplicadoUSD =
      round2(
        Math.max(
          0,
          toNumber(
            feePreviewFormulario
              .montoAplicadoUSD,
            0
          )
        )
      );

    /*
      FONDEO INVERSO
    */
    if (
      feePreviewFormulario
        .esFondeoInverso
    ) {
      const fondeo =
        toNumber(
          formPago
            .fondeoFinancieraUSD,
          0
        );

      if (fondeo <= 0) {
        return "Ingresá un fondeo mayor a cero.";
      }
    }

    if (montoAplicadoUSD <= 0) {
      return "Ingresá un monto mayor a cero.";
    }

    if (
      montoAplicadoUSD >
      maxMontoFormulario + 0.01
    ) {
      return `El monto aplicado a venta no puede superar el saldo real disponible: ${moneyUSD(
        maxMontoFormulario
      )}.`;
    }

    if (
      totalAplicadoPreview >
      totalVentaPreview + 0.01
    ) {
      return `El pago supera el total real de la venta después de aplicar la regla de Tax. Total real: ${moneyUSD(
        totalVentaPreview
      )}.`;
    }

    /*
      CUOTAS DINÁMICAS
    */
    if (
      medioPago ===
        "CREDITO FINANCIERA" &&
      feePreviewFormulario
        .requiereCuotas
    ) {
      const cuotasElegidas =
        toNumber(
          formPago
            .cuotasFinanciacion,
          0
        );

      const cuotasValidas =
        Array.isArray(
          feePreviewFormulario
            .cuotasDisponibles
        )
          ? feePreviewFormulario
              .cuotasDisponibles
          : [];

      if (
        cuotasElegidas <= 0 ||
        (
          cuotasValidas.length > 0 &&
          !cuotasValidas.includes(
            cuotasElegidas
          )
        )
      ) {
        return "Seleccioná una cantidad válida de cuotas.";
      }
    }

    return "";
  };

  const guardarPago = async (e) => {
    e.preventDefault();

    if (soloLectura) {
      setError(
        "Los pagos de esta venta están disponibles solamente para consulta."
      );
      return;
    }

    const msg = validarPago();

    if (msg) {
      alert(msg);
      return;
    }

    const medioPago = clean(formPago.medioPago).toUpperCase();

    const usaResponsablePago =
      permiteResponsablePago(medioPago);

    const usaFee =
      medioPago === "CREDITO FINANCIERA" ||
      medioPago === "CREDITO";

    const montoAplicadoUSD =
      round2(
        Math.max(
          0,
          toNumber(
            feePreviewFormulario
              .montoAplicadoUSD,
            0
          )
        )
      );

    /*
      Diferencias pequeñas de hasta USD 1
      se consideran redondeo operativo.

      Una diferencia mayor no bloquea,
      pero requiere confirmación.
    */
    if (
      feePreviewFormulario
        .esFondeoInverso
    ) {
      const diferencia =
        round2(
          toNumber(
            feePreviewFormulario
              .diferenciaFinancieraUSD,
            0
          )
        );

      if (
        Math.abs(diferencia) > 1
      ) {
        const signo =
          diferencia > 0
            ? "+"
            : "-";

        const confirmar =
          window.confirm(
            "Se detectó una diferencia importante en la financiera.\n\n" +
              `Fondeo informado: ${moneyUSD(
                feePreviewFormulario
                  .fondeoFinancieraUSD
              )}\n` +
              `Fondeo esperado: ${moneyUSD(
                feePreviewFormulario
                  .fondeoEsperadoUSD
              )}\n` +
              `Diferencia financiera: ${signo}${moneyUSD(
                Math.abs(diferencia)
              )}\n\n` +
              "El pago se guardará igualmente y la diferencia quedará registrada.\n\n" +
              "¿Desea continuar?"
          );

        if (!confirmar) {
          return;
        }
      }
    }

    const payload = {
      venta: ventaId,
      medioPago,
      montoUSD: montoAplicadoUSD,

      cuotasFinanciacion:
        medioPago === "CREDITO FINANCIERA"
          ? toNumber(formPago.cuotasFinanciacion, 0)
          : 0,

      feePorcentaje:
        usaFee
          ? feePreviewFormulario.feePorcentaje
          : 0,

      feeMontoUSD:
        usaFee
          ? feePreviewFormulario.feeMontoUSD
          : 0,

      montoFinancieraUSD:
        medioPago === "CREDITO FINANCIERA"
          ? feePreviewFormulario.montoFinancieraUSD
          : 0,

      totalCobrarClienteUSD:
        feePreviewFormulario.totalCobrarClienteUSD,

      financiera:
        medioPago === "CREDITO FINANCIERA"
          ? formPago.financiera
          : null,

      clienteFinanciacion:
        usaResponsablePago
          ? formPago.clienteFinanciacion ||
            clienteCompraId
          : null,

      estadoAcreditacion:
        medioPago === "CREDITO FINANCIERA"
          ? clean(formPago.estadoAcreditacion || "PENDIENTE").toUpperCase()
          : "ACREDITADO",
      fechaPago: formPago.fechaPago || todayLocalISO(),
      observaciones: clean(formPago.observaciones).toUpperCase(),
    };

    /*
      Solamente FONDEO_INVERSO
      envía este dato.
    */
    if (
      feePreviewFormulario
        .esFondeoInverso
    ) {
      payload.fondeoFinancieraUSD =
        feePreviewFormulario
          .fondeoFinancieraUSD;
    }

    try {
      setSaving(true);
      setError("");

      console.log("📦 Payload pago venta:", payload);

      const resp = pagoEditando
        ? await putPagoVenta(getId(pagoEditando), payload)
        : await postPagoVenta(payload);

      if (resp?.venta) {
        setVentaActual(resp.venta);
      }

      if (resp?.resumenPago) {
        setResumenPago(resp.resumenPago);
      }

      limpiarFormularioPago();
      await cargarDatos();

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 700);

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      console.error("❌ Error guardando pago:", err);

      const mensaje =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "No se pudo guardar el pago.";

      setError(mensaje);
      alert(mensaje);
    } finally {
      setSaving(false);
    }
  };

  const acreditarPago = async (pago) => {
    if (soloLectura) {
      setError(
        "Los pagos de esta venta están disponibles solamente para consulta."
      );
      return;
    }
    const id = pago?._id || pago?.id;

    if (!id) return alert("No se encontró el ID del pago.");

    if (
      !window.confirm(
        `¿Confirmás acreditar este pago por ${moneyUSD(pago.montoUSD)}?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const resp = await acreditarPagoVenta(id);

      if (resp?.venta) {
        setVentaActual(resp.venta);
      }

      if (resp?.resumenPago) {
        setResumenPago(resp.resumenPago);
      }

      await cargarDatos();

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      console.error("❌ Error acreditando pago:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo acreditar el pago."
      );
    } finally {
      setSaving(false);
    }
  };

  const marcarFirmadoPago = async (pago) => {
    if (soloLectura) {
      setError(
        "Los pagos de esta venta están disponibles solamente para consulta."
      );
      return;
    }

    if (esManagerMiami) {
      setError(
        "El Manager Miami no tiene permisos para modificar el estado de contrato firmado."
      );
      return;
    }

    const id = pago?._id || pago?.id;

    if (!id) return alert("No se encontró el ID del pago.");

    const firmadoActual = Boolean(pago.contratoFirmado);
    const nuevoValor = !firmadoActual;

    if (
      !window.confirm(
        nuevoValor
          ? "¿Confirmás marcar este contrato como FIRMADO?"
          : "¿Confirmás quitar la marca de contrato firmado?"
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const resp = await marcarContratoFirmadoPagoVenta(id, nuevoValor);

      if (resp?.venta) {
        setVentaActual(resp.venta);
      }

      if (resp?.resumenPago) {
        setResumenPago(resp.resumenPago);
      }

      await cargarDatos();

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      console.error("❌ Error marcando contrato firmado:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo actualizar el estado del contrato."
      );
    } finally {
      setSaving(false);
    }
  };

  const editarPago = (pago) => {
    if (soloLectura) {
      setError(
        "Los pagos de esta venta están disponibles solamente para consulta."
      );
      return;
    }

    const medioPago =
      upper(
        pago?.medioPago ||
          "EFECTIVO EN TIENDA"
      );

    const financieraId =
      medioPago ===
        "CREDITO FINANCIERA"
        ? getId(pago.financiera)
        : "";

    const tieneSnapshotTax =
      typeof pago
        ?.aplicaTaxFinanciera ===
        "boolean";

    const tieneFondeo =
      toNumber(
        pago
          ?.fondeoFinancieraUSD,
        0
      ) > 0;

    /*
      Pago histórico:
      no tenía snapshot ni fondeo.
      Aunque hoy la financiera esté
      migrada, el preview debe seguir
      mostrando la lógica anterior.
    */
    const esHistoricoLegacy =
      medioPago ===
        "CREDITO FINANCIERA" &&
      !tieneSnapshotTax &&
      !tieneFondeo;

    setPagoEditando(pago);

    setFormPago({
      medioPago,

      montoUSD:
        pago?.montoUSD ?? "",

      fondeoFinancieraUSD:
        tieneFondeo
          ? pago.fondeoFinancieraUSD
          : "",

      aplicaTaxFinanciera:
        tieneSnapshotTax
          ? pago.aplicaTaxFinanciera
          : null,

      _forzarLegacyHistorico:
        esHistoricoLegacy,

      _financieraOriginalId:
        financieraId,

      financiera:
        financieraId,

      clienteFinanciacion:
        permiteResponsablePago(
          medioPago
        )
          ? getId(
              pago
                .clienteFinanciacion
            ) ||
            clienteCompraId
          : "",

      cuotasFinanciacion:
        medioPago ===
          "CREDITO FINANCIERA"
          ? pago
              ?.cuotasFinanciacion ||
            ""
          : "",

      estadoAcreditacion:
        medioPago ===
          "CREDITO FINANCIERA"
          ? pago
              ?.estadoAcreditacion ||
            "PENDIENTE"
          : "ACREDITADO",

      fechaPago:
        toDateInputValue(
          pago?.fechaPago
        ) ||
        todayLocalISO(),

      observaciones:
        pago?.observaciones || "",
    });

    setQClienteFinanciacion("");
  };

  const anularPago = async (pago) => {
    if (soloLectura) {
      setError(
        "Los pagos de esta venta están disponibles solamente para consulta."
      );
      return;
    }

    if (esManagerMiami) {
      setError(
        "El Manager Miami no tiene permisos para anular pagos."
      );
      return;
    }
    const id = pago?._id || pago?.id;

    if (!id) return alert("No se encontró el ID del pago.");

    if (
      !window.confirm(
        `¿Está seguro que desea anular el pago por ${moneyUSD(pago.montoUSD)}?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const resp = await deletePagoVenta(id);

      if (resp?.venta) {
        setVentaActual(resp.venta);
      }

      if (resp?.resumenPago) {
        setResumenPago(resp.resumenPago);
      }

      await cargarDatos();

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      console.error("❌ Error anulando pago:", err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo anular el pago."
      );
    } finally {
      setSaving(false);
    }
  };

  const cerrar = () => {
    limpiarFormularioPago();
    setError("");
    handleClose();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={cerrar}
        size="xl"
        centered
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Pagos de Venta</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" /> Cargando pagos...
            </div>
          ) : (
            <div className="container-fluid">
              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}

              {soloLectura && (
                <div className="alert alert-info py-2">
                  Esta venta pertenece a otra sede.
                  Los pagos pueden consultarse, pero no modificarse ni acreditarse.
                </div>
              )}

              {/* ---------------- resumen venta ---------------- */}
              <div className="card shadow-sm mb-3">
                <div className="card-header bg-dark text-white py-2">
                  <b>Resumen de la venta</b>
                </div>

                <div className="card-body py-3">
                  <div className="row">
                    <div className="col-md-2">
                      <small className="text-muted">Venta</small>
                      <div>
                        <b>N° {nroVenta}</b>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Cliente</small>
                      <div>
                        <b>{clienteNombre}</b>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <small className="text-muted">Total</small>
                      <div>
                        <b>{moneyUSD(totalVentaPreview)}</b>

                        {taxNoAplicadoPagoPreviewUSD > 0 && (
                          <div className="small text-success">
                            Tax no aplicado:{" "}
                            {moneyUSD(
                              taxNoAplicadoPagoPreviewUSD
                            )}
                          </div>
)}
                      </div>
                    </div>

                    <div className="col-md-2">
                      <small className="text-muted">Acreditado</small>
                      <div>
                        <b className="text-success">
                          {moneyUSD(totalAcreditado)}
                        </b>
                      </div>
                    </div>

                    <div className="col-md-2">
                      <small className="text-muted">Saldo</small>
                      <div>
                        <b
                          className={
                            saldoPendientePreview > 0
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {moneyUSD(saldoPendientePreview)}
                        </b>
                      </div>
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-3">
                      <small className="text-muted">Aplicado a venta</small>
                      <div>{moneyUSD(totalPagado)}</div>
                    </div>

                    <div className="col-md-3">
                      <small className="text-muted">Fee cliente</small>
                      <div className="text-danger">
                        {moneyUSD(resumenCliente.totalFeeClienteUSD)}
                      </div>
                    </div>

                    <div className="col-md-3">
                      <small className="text-muted">Total cobra cliente</small>
                      <div className="text-primary">
                        {moneyUSD(resumenCliente.totalCobraClienteUSD)}
                      </div>
                    </div>

                    <div className="col-md-3">
                      <small className="text-muted">Estado pago</small>
                      <div>
                        <span className={estadoBadge(estadoPago)}>
                          {estadoPago}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ---------------- nuevo pago ---------------- */}
              <form onSubmit={guardarPago}>
                <div className="card shadow-sm mb-3">
                  <div className="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                    <b>{pagoEditando ? "Editar pago" : "Agregar pago"}</b>

                    {pagoEditando && (
                      <Button
                        type="button"
                        variant="light"
                        size="sm"
                        onClick={limpiarFormularioPago}
                        disabled={saving}
                      >
                        Cancelar edición
                      </Button>
                    )}
                  </div>

                  <div className="card-body py-3">
                    <div className="row">
                      <div className="form-group mt-2 col-md-3">
                        <label>Medio de pago</label>
                        <select
                          className="form-control"
                          name="medioPago"
                          value={formPago.medioPago}
                          onChange={handleChangePago}
                          disabled={formularioBloqueado}
                        >
                          {MEDIOS_PAGO_VENTA.map((medio) => (
                            <option key={medio} value={medio}>
                              {medio}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group mt-2 col-md-2">
                        <label>
                          {feePreviewFormulario
                            .esFondeoInverso
                            ? "Fondeo informado"
                            : "Monto neto aplicado a venta"}
                        </label>

                        <input
                          type="number"
                          className="form-control"
                          name={
                            feePreviewFormulario
                              .esFondeoInverso
                              ? "fondeoFinancieraUSD"
                              : "montoUSD"
                          }
                          value={
                            feePreviewFormulario
                              .esFondeoInverso
                              ? formPago
                                  .fondeoFinancieraUSD
                              : formPago.montoUSD
                          }
                          onChange={handleChangePago}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          disabled={formularioBloqueado}
                        />

                        <small className="text-muted">
                          Disponible para aplicar:{" "}
                          {moneyUSD(
                            maxMontoFormulario
                          )}
                        </small>

                        {feePreviewFormulario
                          .esFondeoInverso &&
                          feePreviewFormulario
                            .fondeoFinancieraUSD >
                            0 && (
                            <div className="mt-1">
                              <div className="small text-danger">
                                Fee{" "}
                                {
                                  feePreviewFormulario
                                    .feePorcentaje
                                }
                                %:{" "}
                                {moneyUSD(
                                  feePreviewFormulario
                                    .feeMontoUSD
                                )}
                              </div>

                              <div className="small text-success fw-bold">
                                Aplicado:{" "}
                                {moneyUSD(
                                  feePreviewFormulario
                                    .montoAplicadoUSD
                                )}
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="form-group mt-2 col-md-2">
                        <label>Fecha pago</label>
                        <input
                          type="date"
                          className="form-control"
                          name="fechaPago"
                          value={formPago.fechaPago}
                          onChange={handleChangePago}
                          disabled={formularioBloqueado}
                        />
                      </div>

                      <div className="form-group mt-2 col-md-3">
                        <label>Financiera</label>
                        <select
                          className="form-control"
                          name="financiera"
                          value={formPago.financiera}
                          onChange={handleChangePago}
                          disabled={
                            formularioBloqueado || !esCreditoFinanciera
                          }
                        >
                          <option value="">Seleccione financiera</option>
                          {financieras.map((f) => {
                            const id = f._id || f.id;
                            const nombre =
                              clean(f.financiera) ||
                              clean(f.razonsocial) ||
                              `Financiera ${f.codfinanciera || ""}`;

                            return (
                              <option key={id} value={id}>
                                {nombre}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="form-group mt-2 col-md-2">
                        <label>Estado</label>
                        <select
                          className="form-control"
                          name="estadoAcreditacion"
                          value={formPago.estadoAcreditacion}
                          onChange={handleChangePago}
                          disabled={
                            formularioBloqueado || !esCreditoFinanciera
                          }
                        >
                          <option value="PENDIENTE">PENDIENTE</option>
                          <option value="ACREDITADO">ACREDITADO</option>
                        </select>
                      </div>

                      {esCreditoFinanciera && (
                        <div className="row mt-2">
                          {feePreviewFormulario.requiereCuotas && (
                            <div className="form-group mt-2 col-md-3">
                              <label>Cuotas</label>
                              <select
                                className="form-control"
                                name="cuotasFinanciacion"
                                value={formPago.cuotasFinanciacion}
                                onChange={handleChangePago}
                                disabled={formularioBloqueado}
                              >
                                <option value="">
                                  Elegir
                                </option>

                                {feePreviewFormulario
                                  .cuotasDisponibles
                                  .map((cantidad) => (
                                    <option
                                      key={cantidad}
                                      value={cantidad}
                                    >
                                      {cantidad} cuotas
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}

                          <div className="form-group mt-2 col-md-9">
                            <div className="border rounded p-2 bg-light">
                              <div className="row g-2">
                                <div className="col-md-3">
                                  <small className="text-muted">
                                    Tarifa
                                  </small>

                                  <div>
                                    <b>
                                      {feePreviewFormulario
                                        .feePorcentaje}
                                      %
                                    </b>
                                  </div>
                                </div>

                                <div className="col-md-3">
                                  <small className="text-muted">
                                    Fee Financiera
                                  </small>

                                  <div>
                                    <b className="text-danger">
                                      {moneyUSD(
                                        feePreviewFormulario
                                          .feeMontoUSD
                                      )}
                                    </b>
                                  </div>
                                </div>

                                {feePreviewFormulario
                                  .esFondeoInverso ? (
                                  <>
                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Neto financiera
                                      </small>

                                      <div>
                                        <b>
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .montoFinancieraUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>

                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Aplicado a venta
                                      </small>

                                      <div>
                                        <b className="text-success">
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .montoAplicadoUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>

                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Fondeo esperado
                                      </small>

                                      <div>
                                        <b>
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .fondeoEsperadoUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>

                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Diferencia financiera
                                      </small>

                                      <div>
                                        <b
                                          style={{
                                            color:
                                              Math.abs(
                                                toNumber(
                                                  feePreviewFormulario
                                                    .diferenciaFinancieraUSD,
                                                  0
                                                )
                                              ) > 1
                                                ? "#dc2626"
                                                : "#d97706",
                                          }}
                                        >
                                          {toNumber(
                                            feePreviewFormulario
                                              .diferenciaFinancieraUSD,
                                            0
                                          ) > 0
                                            ? "+"
                                            : ""}

                                          {moneyUSD(
                                            feePreviewFormulario
                                              .diferenciaFinancieraUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>

                                    {toNumber(
                                      feePreviewFormulario
                                        .ajusteFinancieroUSD,
                                      0
                                    ) > 0 && (
                                      <div className="col-md-3">
                                        <small className="text-muted">
                                          Ajuste financiero
                                        </small>

                                        <div>
                                          <b className="text-danger">
                                            {moneyUSD(
                                              feePreviewFormulario
                                                .ajusteFinancieroUSD
                                            )}
                                          </b>
                                        </div>
                                      </div>
                                    )}

                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Fondeo informado
                                      </small>

                                      <div>
                                        <b className="text-primary">
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .fondeoFinancieraUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Aplicado a venta
                                      </small>

                                      <div>
                                        <b>
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .montoAplicadoUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>

                                    <div className="col-md-3">
                                      <small className="text-muted">
                                        Total cobra cliente
                                      </small>

                                      <div>
                                        <b className="text-primary">
                                          {moneyUSD(
                                            feePreviewFormulario
                                              .totalCobrarClienteUSD
                                          )}
                                        </b>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {esFinancieraSinTax(
                            formPago,
                            financieras
                          ) && (
                            <div className="col-md-12 mt-2">
                              <div className="alert alert-success py-2 mb-0">
                                <b>✓ Esta financiera no paga Tax.</b>

                                {taxNoAplicadoPagoPreviewUSD > 0 && (
                                  <>
                                    {" "}
                                    Tax no aplicado en este pago:{" "}
                                    <b>
                                      {moneyUSD(
                                        taxNoAplicadoPagoPreviewUSD
                                      )}
                                    </b>
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="form-group mt-2 col-md-4">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <label className="mb-0">
                                Buscar acompañante / garante
                              </label>

                              <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                onClick={abrirGaranteIA}
                                disabled={formularioBloqueado}
                              >
                                🤖 IA / Nuevo
                              </Button>
                            </div>

                            <input
                              className="form-control"
                              value={qClienteFinanciacion}
                              onChange={(e) =>
                                setQClienteFinanciacion(
                                  e.target.value
                                )
                              }
                              placeholder="Nombre / documento / teléfono / email"
                              disabled={formularioBloqueado}
                            />
                          </div>

                          <div className="form-group mt-2 col-md-8">
                            <label>Acompañante / garante financiero</label>
                            <select
                              className="form-control"
                              name="clienteFinanciacion"
                              value={formPago.clienteFinanciacion}
                              onChange={handleChangePago}
                              disabled={formularioBloqueado}
                            >
                              <option value="">Seleccione cliente</option>

                              {clientesFiltrados.map((c) => {
                                const id = c._id || c.id;
                                const nombre = getNombreCliente(c);
                                const telefono = clean(c.telefono || c.telefonoPrincipal);

                                return (
                                  <option key={id} value={id}>
                                    {nombre}
                                    {telefono ? ` - ${telefono}` : ""}
                                  </option>
                                );
                              })}
                            </select>

                            <small className="text-muted">
                              Por defecto se usa el cliente comprador. Solo cambiar si la financiación sale a nombre de otra persona.
                            </small>
                          </div>
                        </div>
                      )}

                      {["CREDITO", "DEBITO"].includes(
                        clean(formPago.medioPago).toUpperCase()
                      ) && (
                        <div className="row mt-2">
                          {/* RESUMEN TARJETA */}
                          <div className="col-md-12 mb-2">
                            <div className="border rounded p-2 bg-light">
                              <div className="row g-2">
                                <div className="col-md-4">
                                  <small className="text-muted">
                                    Tarifa
                                  </small>

                                  <div>
                                    <b>
                                      {feePreviewFormulario.feePorcentaje}%
                                    </b>
                                  </div>
                                </div>

                                <div className="col-md-4">
                                  <small className="text-muted">
                                    Fee cliente
                                  </small>

                                  <div>
                                    <b className="text-danger">
                                      {moneyUSD(
                                        feePreviewFormulario.feeMontoUSD
                                      )}
                                    </b>
                                  </div>
                                </div>

                                <div className="col-md-4">
                                  <small className="text-muted">
                                    Total cobra cliente
                                  </small>

                                  <div>
                                    <b className="text-primary">
                                      {moneyUSD(
                                        feePreviewFormulario.totalCobrarClienteUSD
                                      )}
                                    </b>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="form-group mt-2 col-md-4">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <label className="mb-0">
                                Buscar acompañante / garante
                              </label>

                              <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                onClick={abrirGaranteIA}
                                disabled={formularioBloqueado}
                              >
                                🤖 IA / Nuevo
                              </Button>
                            </div>

                            <input
                              className="form-control"
                              value={qClienteFinanciacion}
                              onChange={(e) =>
                                setQClienteFinanciacion(
                                  e.target.value
                                )
                              }
                              placeholder="Nombre / documento / teléfono / email"
                              disabled={formularioBloqueado}
                            />
                          </div>

                          <div className="form-group mt-2 col-md-8">
                            <label>
                              ¿Quién realiza este pago?
                            </label>

                            <select
                              className="form-control"
                              name="clienteFinanciacion"
                              value={formPago.clienteFinanciacion}
                              onChange={handleChangePago}
                              disabled={formularioBloqueado}
                            >
                              <option value="">
                                Seleccione cliente / garante
                              </option>

                              {clientesFiltrados.map((c) => {
                                const id = c._id || c.id;

                                const nombre =
                                  getNombreCliente(c);

                                const dni = clean(
                                  c.dni ||
                                    c.documento ||
                                    c.idDocumento
                                );

                                const telefono = clean(
                                  c.telefono1 ||
                                    c.telefonoPrincipal ||
                                    c.telefono
                                );

                                return (
                                  <option
                                    key={id}
                                    value={id}
                                  >
                                    {nombre}
                                    {dni
                                      ? ` - DNI: ${dni}`
                                      : ""}
                                    {telefono
                                      ? ` - ${telefono}`
                                      : ""}
                                  </option>
                                );
                              })}
                            </select>

                            <small className="text-muted">
                              Por defecto se utiliza el cliente comprador.
                              Cambialo solamente si el pago lo realiza un
                              acompañante / garante.
                            </small>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="form-group mt-2 col-md-10">
                        <label>Observaciones</label>
                        <input
                          className="form-control"
                          name="observaciones"
                          value={formPago.observaciones}
                          onChange={handleChangePago}
                          placeholder="Observaciones del pago"
                          disabled={formularioBloqueado}
                        />
                      </div>

                      <div className="form-group mt-4 col-md-2 d-flex align-items-end">
                        <Button
                          type="submit"
                          variant="dark"
                          className="w-100"
                          disabled={formularioBloqueado}
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" /> Guardando
                            </>
                          ) : (
                            pagoEditando ? "Actualizar" : "Agregar"
                          )}
                        </Button>
                      </div>
                    </div>

                    {!pagoEditando && saldoPendiente <= 0 && (
                      <div className="alert alert-success py-2 mt-3 mb-0">
                        La venta ya tiene pagos cargados por el total. Podés editar, acreditar o anular pagos existentes.
                      </div>
                    )}
                  </div>
                </div>
              </form>

              {/* ---------------- listado pagos ---------------- */}
              <div className="card shadow-sm">
                <div className="card-header bg-light py-2">
                  <b>Pagos cargados</b>
                </div>

                <div className="card-body p-0">
                  <div style={{ overflowX: "auto" }}>
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th className="text-center">N°</th>
                          <th className="text-center">Fecha</th>
                          <th>Medio</th>
                          <th>Financiera</th>
                          <th>Acomp./Garante</th>
                          <th className="text-end">Aplicado</th>
                          <th className="text-end">Fee</th>
                          <th className="text-end">Total cliente</th>
                          <th className="text-center">Estado</th>
                          <th className="text-center">Acreditación</th>
                          <th className="text-center">Firmado</th>
                          <th>Obs.</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>

                      <tbody>
                        {pagos.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="text-center py-3">
                              No hay pagos cargados.
                            </td>
                          </tr>
                        ) : (
                          pagos.map((p) => {
                            const id = p._id || p.id;
                            const estado = clean(
                              p.estadoAcreditacion
                            ).toUpperCase();

                            const pendiente = estado === "PENDIENTE";

                            return (
                              <tr key={id || p.nropago}>
                                <td className="text-center">
                                  {p.nropago || "-"}
                                </td>

                                <td className="text-center">
                                  {toDateInputValue(p.fechaPago) || "-"}
                                </td>

                                <td>{p.medioPago || "-"}</td>

                                <td>{getNombreFinanciera(p.financiera)}</td>

                                <td>{getNombreClienteFinanciacionPago(p)}</td>

                                <td className="text-end">
                                  <b>{moneyUSD(p.montoUSD)}</b>
                                </td>

                                <td className="text-end">
                                  {toNumber(p.feeMontoUSD, 0) > 0
                                    ? moneyUSD(p.feeMontoUSD)
                                    : "-"}
                                </td>

                                <td className="text-end">
                                  {moneyUSD(
                                    toNumber(p.totalCobrarClienteUSD, 0) > 0
                                      ? p.totalCobrarClienteUSD
                                      : round2(
                                          toNumber(p.montoUSD, 0) +
                                            toNumber(p.feeMontoUSD, 0)
                                        )
                                  )}
                                </td>

                                <td className="text-center">
                                  <span className={estadoBadge(estado)}>
                                    {estado || "-"}
                                  </span>
                                </td>

                                <td className="text-center">
                                  {toDateInputValue(p.fechaAcreditacion) || "-"}
                                </td>

                                <td className="text-center">
                                  {clean(p.medioPago).toUpperCase() === "CREDITO FINANCIERA" ? (
                                    <Button
                                      variant={p.contratoFirmado ? "success" : "outline-warning"}
                                      size="sm"
                                      onClick={() => marcarFirmadoPago(p)}
                                      disabled={
                                        soloLectura ||
                                        esManagerMiami ||
                                        saving
                                      }
                                      title={
                                        p.contratoFirmado
                                          ? "Contrato firmado. Click para desmarcar."
                                          : "Contrato pendiente. Click para marcar firmado."
                                      }
                                      style={{
                                        minWidth: 42,
                                        fontWeight: 900,
                                      }}
                                    >
                                      {p.contratoFirmado ? "SÍ" : "NO"}
                                    </Button>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td
                                  style={{
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={p.observaciones || ""}
                                >
                                  {p.observaciones || "-"}
                                </td>

                                <td className="text-center">
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 5,
                                      justifyContent: "center",
                                    }}
                                  >
                                    {pendiente && (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => acreditarPago(p)}
                                        disabled={soloLectura || saving}
                                        title="Acreditar pago"
                                      >
                                        ✓
                                      </Button>
                                    )}

                                    <Button
                                      variant="warning"
                                      size="sm"
                                      onClick={() => editarPago(p)}
                                      disabled={soloLectura || saving}
                                      title="Editar pago"
                                    >
                                      <i
                                        className="fa fa-pencil"
                                        aria-hidden="true"
                                      />
                                    </Button>

                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => anularPago(p)}
                                      disabled={
                                        soloLectura ||
                                        esManagerMiami ||
                                        saving
                                      }
                                      title="Anular pago"
                                    >
                                      <i
                                        className="fa fa-trash-o"
                                        aria-hidden="true"
                                      />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={cerrar} disabled={saving}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalGaranteFinanciacion
        show={showGaranteIA}
        onClose={cerrarGaranteIA}
        onSaved={
          onGaranteIAGuardado
        }
      />

      {/* ---------------- Modal éxito ---------------- */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>💵</div>
          <h5 className="mt-2">¡Pago guardado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}